import type { ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Input,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Autocomplete,
  AutocompleteItem,
} from "@heroui/react";
import type { Selection } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { FaUserTag } from "react-icons/fa";
import { SearchIcon } from "./SearchIcon";
import { ChevronDownIcon } from "./ChevronDownIcon";
import { capitalize } from "@/utils/utils";
import OrdersDateFilter from "./OrdersDateFilter";
import type { ColumnDef, StatusOption, FilterListItem, DateRangeValue } from "./types";

interface AssignmentOption {
  uid: string;
  name: string;
}

interface OrdersFiltersProps {
  filterValue: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  clientList: FilterListItem[];
  clientListFilter: string | null;
  onClientListFilterChange: (value: string | null) => void;
  statusOptions: StatusOption[];
  statusFilter: Selection;
  onStatusFilterChange: (value: Selection) => void;
  showEntityFilter: boolean;
  entityList: FilterListItem[];
  entityListFilter: Selection;
  onEntityListFilterChange: (value: Selection) => void;
  showAssignmentFilter: boolean;
  assignmentList: AssignmentOption[];
  assignmentFilter: Selection;
  onAssignmentFilterChange: (value: Selection) => void;
  periodFilterInterval: DateRangeValue | null;
  onPeriodFilterChange: (value: DateRangeValue | null) => void;
  onRadioChange: (event: ChangeEvent<HTMLInputElement>) => void;
  columns: ColumnDef[];
  visibleColumns: Selection;
  onVisibleColumnsChange: (value: Selection) => void;
  totalOrders: number;
  rowsPerPage: number;
  onRowsPerPageChange: (e: ChangeEvent<HTMLSelectElement>) => void;
}

export function OrdersFilters({
  filterValue,
  onSearchChange,
  onClearSearch,
  clientList,
  clientListFilter,
  onClientListFilterChange,
  statusOptions,
  statusFilter,
  onStatusFilterChange,
  showEntityFilter,
  entityList,
  entityListFilter,
  onEntityListFilterChange,
  showAssignmentFilter,
  assignmentList,
  assignmentFilter,
  onAssignmentFilterChange,
  periodFilterInterval,
  onPeriodFilterChange,
  onRadioChange,
  columns,
  visibleColumns,
  onVisibleColumnsChange,
  totalOrders,
  rowsPerPage,
  onRowsPerPageChange,
}: Readonly<OrdersFiltersProps>) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <Input
            isClearable
            classNames={{
              base: "w-full min-w-[45%]",
              inputWrapper: [
                "border-0",
                "bg-white",
                "h-9",
                "!min-h-9",
                "hover:bg-white",
                "group-data-[hover=true]:bg-white",
                "group-data-[focus=true]:bg-white",
              ],
              input: "text-vistagrey",
            }}
            placeholder={t("search_placeholder")}
            size="sm"
            startContent={<SearchIcon className="text-default-300" />}
            value={filterValue}
            variant="flat"
            radius="full"
            onClear={onClearSearch}
            onValueChange={onSearchChange}
            data-cy="headerSearchInput"
          />
          <div className="flex gap-3">
            {/* Client filter */}
            <Autocomplete
              aria-label={t("clients")}
              placeholder={t("clients")}
              size="sm"
              variant="flat"
              radius="full"
              className="hidden sm:flex min-w-[180px]"
              selectedKey={clientListFilter}
              onSelectionChange={(key) => onClientListFilterChange(key as string | null)}
              isClearable
              classNames={{
                base: "h-9",
                listboxWrapper: "max-h-[320px]",
                selectorButton: "text-vistagrey",
              }}
              inputProps={{
                classNames: {
                  inputWrapper: [
                    "h-9",
                    "min-h-unit-9",
                    "bg-white",
                    "hover:bg-white",
                    "group-data-[hover=true]:bg-white",
                    "group-data-[focus=true]:bg-white",
                  ],
                  input: "text-vistagrey text-small font-medium placeholder:text-vistagrey placeholder:font-medium",
                },
              }}
            >
              {clientList.map((client) => (
                <AutocompleteItem
                  key={client.uid || uuidv4()}
                  textValue={client.name || t("not_applicable")}
                  className="text-xs"
                >
                  {client.name ? client.name.charAt(0).toUpperCase() + client.name.slice(1).toLowerCase() : ""}
                </AutocompleteItem>
              ))}
            </Autocomplete>

            {/* Status filter */}
            <Dropdown>
              <DropdownTrigger className="hidden sm:flex">
                <Button
                  endContent={<ChevronDownIcon className="text-vistagrey" />}
                  size="sm"
                  variant="flat"
                  radius="full"
                  className="h-9 min-w-fit px-4 bg-white text-vistagrey text-small"
                  data-cy="orders__statusDisplay"
                >
                  {t("status")}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection
                aria-label={t("table_columns")}
                closeOnSelect={false}
                selectedKeys={statusFilter}
                selectionMode="multiple"
                onSelectionChange={onStatusFilterChange}
              >
                {statusOptions.map((status) => (
                  <DropdownItem
                    key={status.uid || uuidv4()}
                    textValue={status.name || t("not_applicable")}
                    className="capitalize"
                  >
                    {capitalize(status.name)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            {/* Entity filter */}
            {showEntityFilter && entityList.length > 1 && (
              <Dropdown>
                <DropdownTrigger className="hidden sm:flex">
                  <Button
                    endContent={<ChevronDownIcon className="text-vistagrey" />}
                    size="sm"
                    variant="flat"
                    radius="full"
                    className="h-9 min-w-fit px-4 bg-white text-vistagrey text-small"
                    data-cy="entity__filter"
                  >
                    {t("entity")}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  disallowEmptySelection
                  aria-label={t("table_columns")}
                  closeOnSelect={false}
                  selectedKeys={entityListFilter}
                  selectionMode="multiple"
                  onSelectionChange={onEntityListFilterChange}
                >
                  {entityList.map((entity) => (
                    <DropdownItem
                      key={entity.uid || uuidv4()}
                      textValue={entity.name || t("not_applicable")}
                      className="capitalize"
                    >
                      {capitalize(entity.name)}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            )}

          </div>
          <OrdersDateFilter
            value={periodFilterInterval}
            onChange={onPeriodFilterChange}
            onRadioChange={onRadioChange}
          />
          {/* Assignment filter */}
          {showAssignmentFilter && assignmentList.length > 0 && (
            <Dropdown>
              <DropdownTrigger className="hidden sm:flex">
                <Button
                  isIconOnly
                  size="sm"
                  variant="flat"
                  radius="full"
                  className="h-9 w-9 bg-white text-vistagrey"
                  data-cy="assignment__filter"
                >
                  <FaUserTag className="text-base" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection
                aria-label={t("assigned_to")}
                closeOnSelect={false}
                selectedKeys={assignmentFilter}
                selectionMode="multiple"
                onSelectionChange={onAssignmentFilterChange}
              >
                {assignmentList.map((user) => (
                  <DropdownItem
                    key={user.uid}
                    textValue={user.name}
                    className="capitalize"
                  >
                    {capitalize(user.name)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
        <div className="flex gap-3">
          <Dropdown>
            <DropdownTrigger className="hidden sm:flex">
              <Button
                endContent={<ChevronDownIcon className="text-small" />}
                size="sm"
                variant="flat"
                radius="full"
                className="h-9 bg-white text-vistagrey text-small"
                data-cy="orders__columnDisplay"
              >
                {t("columns")}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              disallowEmptySelection
              aria-label={t("table_columns")}
              closeOnSelect={false}
              selectedKeys={visibleColumns}
              selectionMode="multiple"
              onSelectionChange={onVisibleColumnsChange}
              className="overflow-scroll max-h-[45vh]"
            >
              {columns
                .filter((column) => !column.alwaysVisible)
                .map((column) => (
                <DropdownItem
                  key={column.uid}
                  textValue={column.name}
                  className="capitalize"
                  data-cy={`orders__columnDisplay-${column.uid}`}
                >
                  {capitalize(column.name)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-default-400 text-small">
          {t("total_orders", { count: totalOrders })}
        </span>
        <label className="flex items-center text-default-400 text-small">
          <span className="mr-1">{t("orders_per_page")} :</span>
          <select
            className="bg-transparent outline-none text-default-400 text-small"
            onChange={onRowsPerPageChange}
            value={rowsPerPage}
          >
            <option value="15">15</option>
            <option value="50">50</option>
          </select>
        </label>
      </div>
    </div>
  );
};

export default OrdersFilters;
